import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, ArrowLeft, Gift } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { GlobalSettings } from "@/components/GlobalSettings";

export default function AuthPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = location.state?.from || "/feed";

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setMode("register");
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success(t("common.toast_welcome_back"));
      } else {
        await register(email, password, name, referralCode || undefined);
        toast.success(referralCode ? `Welcome — $5 credit unlocks after your first ticket!` : "Account created");
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white relative grain-overlay flex flex-col">
      <div className="pointer-events-none absolute -top-32 -left-16 w-80 h-80 rounded-full bg-rose-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-16 w-80 h-80 rounded-full bg-amber-500/15 blur-3xl" />

      <div className="relative z-10 flex items-center justify-between pr-4">
        <Link to="/" className="p-4 inline-flex items-center gap-2 text-slate-400 hover:text-white w-fit" data-testid="auth-back">
          <ArrowLeft size={18} /> <span className="text-sm">{t("auth.back")}</span>
        </Link>
        <GlobalSettings />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center">
              <Zap size={22} className="text-white" strokeWidth={2.8} fill="white" />
            </span>
            <span className="font-display font-black text-xl">LastMinute<span className="text-rose-400">.</span></span>
          </div>

          <h1 className="font-display font-black text-3xl mb-2 tracking-tight">
            {mode === "login" ? t("auth.welcome_back") : t("auth.create_account")}
          </h1>
          <p className="text-slate-400 mb-8 text-sm">
            {mode === "login" ? t("auth.welcome_sub") : t("auth.create_sub")}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-400 font-mono">{t("auth.name")}</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("auth.name_placeholder")}
                  required
                  className="bg-[#131A26] border-white/10 h-12"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-400 font-mono">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.email_placeholder")}
                required
                className="bg-[#131A26] border-white/10 h-12"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-400 font-mono">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.password_placeholder")}
                minLength={8}
                required
                className="bg-[#131A26] border-white/10 h-12"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full h-12 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-xl"
            >
              {busy ? t("auth.please_wait") : mode === "login" ? t("auth.signin") : t("auth.create")}
            </Button>

            {mode === "register" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono flex items-center gap-1.5">
                  <Gift size={12} className="text-amber-400" /> Referral code (optional)
                </Label>
                <Input
                  data-testid="auth-referral-input"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="EVITE1234"
                  maxLength={12}
                  className="bg-[#131A26] border-amber-500/25 h-11 font-mono uppercase"
                />
                {referralCode && (
                  <p className="text-[11px] text-amber-400/90">Both of you get $5 credit after your first ticket purchase.</p>
                )}
              </div>
            )}
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">{t("auth.or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            data-testid="auth-google-btn"
            className="w-full h-12 rounded-xl bg-white text-slate-900 font-bold flex items-center justify-center gap-3 hover:bg-slate-100 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("auth.google")}
          </button>

          <div className="mt-6 text-sm text-slate-400 text-center">
            {mode === "login" ? t("auth.no_account") : t("auth.have_account")}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-toggle-mode"
              className="text-rose-400 hover:text-rose-300 font-semibold"
            >
              {mode === "login" ? t("auth.signup") : t("auth.signin")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
